import React, { useEffect, useRef, useState } from 'react';
import { homeShareUrl } from '../../navigation/publicRoutes';
import styles from './ShareListingButton.module.css';

/** Statuses with a public page (backend PUBLIC_SHARE_LISTING_STATUSES). */
export const SHAREABLE_STATUSES = ['active', 'pending'];

/**
 * Whether a listing has a public share page.
 *
 * @param {{listing_status?: string}} house
 * @returns {boolean}
 */
export function isShareableListing(house) {
  return SHAREABLE_STATUSES.includes(String(house?.listing_status || '').toLowerCase());
}

const prefersNativeShare = () => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  // The OS share sheet suits phones; on desktop a copied link is what people expect.
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
};

/**
 * "Share" for a listing's public page (/homes/:id): opens the phone's share
 * sheet, or copies the link. When the clipboard is blocked, the link is shown
 * selected so it can be copied by hand.
 *
 * @param {object} props
 * @param {number} props.houseId
 * @param {string} [props.address] - Used as the share-sheet title.
 * @param {string} [props.className] - Button class (so callers keep their own button look).
 * @param {string} [props.label='Share']
 * @param {boolean} [props.showOpenLink=false] - After copying, offer "Open public page".
 */
export default function ShareListingButton({
  houseId,
  address = '',
  className = '',
  label = 'Share',
  showOpenLink = false,
}) {
  const [status, setStatus] = useState('idle'); // idle | copied | shared | manual
  const resetRef = useRef(null);
  const manualRef = useRef(null);
  const url = homeShareUrl(houseId);

  useEffect(() => () => clearTimeout(resetRef.current), []);
  useEffect(() => {
    if (status === 'manual' && manualRef.current) manualRef.current.select();
  }, [status]);

  const settle = (next) => {
    setStatus(next);
    clearTimeout(resetRef.current);
    if (next === 'copied' || next === 'shared') {
      resetRef.current = setTimeout(() => setStatus('idle'), 6000);
    }
  };

  const handleClick = async () => {
    if (prefersNativeShare()) {
      try {
        await navigator.share({ title: address ? `${address} on cutthe6.com` : 'A home on cutthe6.com', url });
        settle('shared');
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return; // they closed the sheet
        // Fall through to copying.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      settle('copied');
    } catch {
      settle('manual');
    }
  };

  return (
    <span className={styles.wrap}>
      <button
        type="button"
        className={className || styles.button}
        onClick={handleClick}
        aria-label={`${label}: copy the public link to this listing`}
        data-testid={`share-listing-${houseId}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
          <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
        </svg>
        <span>{label}</span>
      </button>
      <span className={styles.status} role="status" aria-live="polite">
        {status === 'copied' && 'Link copied'}
        {status === 'shared' && 'Shared'}
      </span>
      {status === 'manual' && (
        <label className={styles.manual}>
          <span className={styles.manualLabel}>Copy this link</span>
          <input
            ref={manualRef}
            className={styles.manualInput}
            readOnly
            value={url}
            onFocus={(e) => e.target.select()}
          />
        </label>
      )}
      {showOpenLink && status !== 'idle' && (
        <a className={styles.openLink} href={url} target="_blank" rel="noopener noreferrer">
          Open public page
        </a>
      )}
    </span>
  );
}
